import { captureMessage } from '@sentry/react';
import { vercelEdgeConfigClient } from 'init';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { isNonNullish, isNullish } from 'remeda';
import apiService from 'shared/service/api.service';
import { z } from 'zod/v4';

import { isDemo, isProduction } from './environment';

const FeatureFlagConfigSchema = z.record(z.string(), z.array(z.string()));
type FeatureFlagConfig = z.infer<typeof FeatureFlagConfigSchema>;

const featureFlagsAtom = atom<FeatureFlagConfig | null>(null);

/**
 * If the flag cannot be found, returns false.
 *
 * Then, if we are in a public-facing environment, returns whether the flag is on for this tenant.
 *
 * Otherwise, if we are in a development environment, always returns true.
 *
 * After setting a new flag in Vercel, all recovr tabs must be closed fully for the new flag to be applied.
 *
 * @param slug - The slug of the feature flag to check, as set in Vercel Edge Config.
 */
export const useIsFeatureFlagEnabled = (slug: string): boolean => {
  const featureFlags = useAtomValue(featureFlagsAtom);
  if (isNullish(featureFlags)) return false;
  if (isNullish(featureFlags[slug])) return false;

  if (isProduction() || isDemo()) return featureFlags[slug].includes(apiService.tenant);

  return true;
};

export const useAreFeatureFlagsLoaded = (): boolean => isNonNullish(useAtomValue(featureFlagsAtom));

export const useLoadFeatureFlags = () => {
  const setFeatureFlags = useSetAtom(featureFlagsAtom);
  const cachedFeatureFlags = window.sessionStorage.getItem('featureFlags');

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      const flags = await getFeatureFlags(cachedFeatureFlags);

      if (flags === edgeConfigUnreachable) {
        setFeatureFlags({});
        return;
      }

      const validationResult = FeatureFlagConfigSchema.safeParse(flags);

      if (validationResult.success) {
        setFeatureFlags(validationResult.data);
        window.sessionStorage.setItem('featureFlags', JSON.stringify(validationResult.data));
      } else handleError(validationResult.error);
    };

    fetchFeatureFlags();
  }, [setFeatureFlags]);
};

const edgeConfigUnreachable = Symbol('edgeConfigUnreachable');

async function getFeatureFlags(cachedFeatureFlags: string | null): Promise<unknown> {
  if (isNonNullish(cachedFeatureFlags)) return JSON.parse(cachedFeatureFlags);

  try {
    return await vercelEdgeConfigClient.get('featureFlags');
  } catch (error) {
    if (error instanceof TypeError) return edgeConfigUnreachable;
    throw error;
  }
}

function handleError(error: z.ZodError<Record<string, string[]>>) {
  console.error("Couldn't parse feature flags object from Vercel Edge Config.", error);
  captureMessage(
    "WARNING: Couldn't parse feature flags object from Vercel Edge Config. Zod error attached to sentry event.",
    { extra: { zodError: error } },
  );
}
